import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, FileText, CheckCircle, AlertTriangle, CreditCard, Ban, UserX } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const TermsOfService = () => {
  const { i18n } = useTranslation();
  const isFr = i18n.language === 'fr';

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="flex items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-md bg-primary flex items-center justify-center">
                <span className="font-heading font-bold text-xl text-primary-foreground">M</span>
              </div>
              <span className="font-heading font-bold text-xl">MOTO SOCIAL</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-8 py-8">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          {isFr ? 'Retour à l\'accueil' : 'Back to home'}
        </Link>

        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
            <FileText className="w-6 h-6 text-primary" />
          </div>
          <h1 className="font-heading font-bold text-3xl md:text-4xl uppercase tracking-tight">
            {isFr ? 'Conditions Générales d\'Utilisation' : 'Terms of Service'}
          </h1>
        </div>

        <div className="space-y-8">
          {/* Article 1 - Objet */}
          <Card>
            <CardHeader>
              <CardTitle>Article 1 - {isFr ? 'Objet' : 'Purpose'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground">
              <p>
                {isFr
                  ? "Les présentes Conditions Générales d'Utilisation (CGU) ont pour objet de définir les modalités et conditions d'utilisation de la plateforme Moto Social, accessible à l'adresse motosocial.eu."
                  : "These Terms of Service (ToS) define the terms and conditions of use of the Moto Social platform, accessible at motosocial.eu."}
              </p>
              <p>
                {isFr
                  ? "Moto Social est une plateforme de réseau social dédiée aux passionnés de moto, permettant de :"
                  : "Moto Social is a social networking platform dedicated to motorcycle enthusiasts, allowing users to:"}
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>{isFr ? 'Créer un profil personnel et présenter ses motos' : 'Create a personal profile and showcase motorcycles'}</li>
                <li>{isFr ? 'Partager des moments et des balades' : 'Share moments and rides'}</li>
                <li>{isFr ? 'Découvrir et participer à des événements moto' : 'Discover and participate in motorcycle events'}</li>
                <li>{isFr ? 'Trouver des lieux adaptés aux motards' : 'Find biker-friendly places'}</li>
                <li>{isFr ? 'Acheter et vendre des motos, pièces et accessoires' : 'Buy and sell motorcycles, parts, and accessories'}</li>
              </ul>
            </CardContent>
          </Card>

          {/* Article 2 - Acceptation */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                Article 2 - {isFr ? 'Acceptation des CGU' : 'Acceptance of Terms'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground">
              <p>
                {isFr
                  ? "L'inscription et l'utilisation de la plateforme impliquent l'acceptation pleine et entière des présentes CGU. Si vous n'acceptez pas ces conditions, vous ne devez pas utiliser nos services."
                  : "Registration and use of the platform implies full acceptance of these Terms. If you do not accept these terms, you must not use our services."}
              </p>
              <p>
                {isFr
                  ? "Moto Social se réserve le droit de modifier les présentes CGU à tout moment. Les utilisateurs seront informés de toute modification par email ou notification sur la plateforme."
                  : "Moto Social reserves the right to modify these Terms at any time. Users will be informed of any changes via email or platform notification."}
              </p>
            </CardContent>
          </Card>

          {/* Article 3 - Inscription */}
          <Card>
            <CardHeader>
              <CardTitle>Article 3 - {isFr ? 'Inscription et compte utilisateur' : 'Registration and User Account'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground">
              <p>
                {isFr
                  ? "Pour accéder aux fonctionnalités de Moto Social, l'utilisateur doit créer un compte en fournissant des informations exactes et à jour."
                  : "To access Moto Social features, the user must create an account by providing accurate and up-to-date information."}
              </p>
              <p><strong className="text-foreground">{isFr ? 'Conditions d\'inscription' : 'Registration requirements'}:</strong></p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>{isFr ? 'Être âgé d\'au moins 18 ans' : 'Be at least 18 years old'}</li>
                <li>{isFr ? 'Fournir une adresse email valide' : 'Provide a valid email address'}</li>
                <li>{isFr ? 'Choisir un mot de passe sécurisé' : 'Choose a secure password'}</li>
                <li>{isFr ? 'Ne pas créer de faux profils' : 'Do not create fake profiles'}</li>
              </ul>
              <p>
                {isFr
                  ? "L'utilisateur est responsable de la confidentialité de ses identifiants de connexion."
                  : "The user is responsible for the confidentiality of their login credentials."}
              </p>
            </CardContent>
          </Card>

          {/* Article 4 - Utilisation */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-500" />
                Article 4 - {isFr ? 'Règles d\'utilisation' : 'Usage Rules'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground">
              <p><strong className="text-foreground">{isFr ? 'Il est interdit de' : 'It is prohibited to'}:</strong></p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>{isFr ? 'Publier du contenu illégal, diffamatoire, haineux ou discriminatoire' : 'Post illegal, defamatory, hateful, or discriminatory content'}</li>
                <li>{isFr ? 'Harceler ou menacer d\'autres utilisateurs' : 'Harass or threaten other users'}</li>
                <li>{isFr ? 'Publier du spam ou de la publicité non autorisée' : 'Post spam or unauthorized advertising'}</li>
                <li>{isFr ? 'Usurper l\'identité d\'autrui' : 'Impersonate others'}</li>
                <li>{isFr ? 'Tenter de pirater ou compromettre la sécurité du site' : 'Attempt to hack or compromise site security'}</li>
                <li>{isFr ? 'Utiliser des robots ou scripts automatisés' : 'Use bots or automated scripts'}</li>
                <li>{isFr ? 'Publier des annonces frauduleuses sur le marketplace' : 'Post fraudulent listings on the marketplace'}</li>
              </ul>
            </CardContent>
          </Card>

          {/* Article 5 - Marketplace */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-primary" />
                Article 5 - {isFr ? 'Marketplace et paiements' : 'Marketplace and Payments'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground">
              <p>
                {isFr
                  ? "Moto Social propose une marketplace permettant aux utilisateurs d'acheter et vendre des motos, pièces et accessoires."
                  : "Moto Social offers a marketplace allowing users to buy and sell motorcycles, parts, and accessories."}
              </p>
              <p><strong className="text-foreground">{isFr ? 'Commission' : 'Commission'}:</strong></p>
              <p>
                {isFr
                  ? "Une commission de 10% est prélevée sur chaque transaction effectuée via la plateforme. Cette commission couvre les frais de paiement sécurisé et les services de la plateforme."
                  : "A 10% commission is charged on each transaction made through the platform. This commission covers secure payment fees and platform services."}
              </p>
              <p><strong className="text-foreground">{isFr ? 'Paiements' : 'Payments'}:</strong></p>
              <p>
                {isFr
                  ? "Les paiements sont traités de manière sécurisée via Stripe. Moto Social n'a jamais accès à vos informations bancaires complètes."
                  : "Payments are processed securely via Stripe. Moto Social never has access to your complete banking information."}
              </p>
              <p><strong className="text-foreground">{isFr ? 'Responsabilité' : 'Liability'}:</strong></p>
              <p>
                {isFr
                  ? "Les vendeurs sont seuls responsables de l'exactitude des annonces et de l'état des produits vendus. Moto Social agit uniquement en tant qu'intermédiaire."
                  : "Sellers are solely responsible for the accuracy of listings and the condition of products sold. Moto Social acts only as an intermediary."}
              </p>
            </CardContent>
          </Card>

          {/* Article 6 - Contenu utilisateur */}
          <Card>
            <CardHeader>
              <CardTitle>Article 6 - {isFr ? 'Contenu utilisateur' : 'User Content'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground">
              <p>
                {isFr
                  ? "En publiant du contenu sur Moto Social, vous accordez à la plateforme une licence non exclusive, gratuite et mondiale pour utiliser, reproduire et afficher ce contenu dans le cadre du service."
                  : "By posting content on Moto Social, you grant the platform a non-exclusive, free, worldwide license to use, reproduce, and display this content as part of the service."}
              </p>
              <p>
                {isFr
                  ? "Vous restez propriétaire de vos contenus et pouvez les supprimer à tout moment."
                  : "You remain the owner of your content and can delete it at any time."}
              </p>
              <p>
                {isFr
                  ? "Moto Social se réserve le droit de supprimer tout contenu contraire aux présentes CGU sans préavis."
                  : "Moto Social reserves the right to remove any content that violates these Terms without notice."}
              </p>
            </CardContent>
          </Card>

          {/* Article 7 - Suspension */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserX className="w-5 h-5 text-red-500" />
                Article 7 - {isFr ? 'Suspension et résiliation' : 'Suspension and Termination'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground">
              <p>
                {isFr
                  ? "Moto Social peut suspendre ou résilier votre compte en cas de violation des présentes CGU, notamment en cas de :"
                  : "Moto Social may suspend or terminate your account in case of violation of these Terms, including:"}
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>{isFr ? 'Publication de contenu interdit' : 'Publication of prohibited content'}</li>
                <li>{isFr ? 'Comportement abusif envers d\'autres utilisateurs' : 'Abusive behavior towards other users'}</li>
                <li>{isFr ? 'Fraude ou tentative de fraude' : 'Fraud or attempted fraud'}</li>
                <li>{isFr ? 'Utilisation de faux profils' : 'Use of fake profiles'}</li>
              </ul>
              <p>
                {isFr
                  ? "Vous pouvez supprimer votre compte à tout moment depuis vos paramètres de profil."
                  : "You can delete your account at any time from your profile settings."}
              </p>
            </CardContent>
          </Card>

          {/* Article 8 - Droit applicable */}
          <Card>
            <CardHeader>
              <CardTitle>Article 8 - {isFr ? 'Droit applicable' : 'Applicable Law'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground">
              <p>
                {isFr
                  ? "Les présentes CGU sont régies par le droit français. Tout litige relatif à l'interprétation ou l'exécution des présentes sera soumis aux tribunaux français compétents."
                  : "These Terms are governed by French law. Any dispute relating to the interpretation or execution of these Terms will be submitted to the competent French courts."}
              </p>
            </CardContent>
          </Card>

          {/* Date de mise à jour */}
          <p className="text-sm text-muted-foreground text-center pt-8">
            {isFr ? 'Dernière mise à jour' : 'Last updated'}: {new Date().toLocaleDateString(isFr ? 'fr-FR' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
      </main>
    </div>
  );
};

export default TermsOfService;
